import type { IAny } from '~/helpers/types';
import type { IAppRegion } from '~/lib/appSettings';
import type { IStorage, KeyManifest } from '~/lib/storage';
import type { IAuthCacheKeyData } from './AuthCacheKey';
import { AuthCacheKey } from './AuthCacheKey';

const DEFAULT_EXPIRY_ADJUSTMENT_SECONDS = 0;

export type IAuthDecodedToken = Record<string, never>;

type IWrappedCacheEntry<TBody = IAny> = {
  body: TBody;
  expiresAt?: number;
};

export type IAuthCacheEntry<TAuthUser> = IAuthCacheKeyData & {
  region: IAppRegion;
  authToken: string;
  expiresIn?: number;
  user: TAuthUser;
  isImpersonated?: boolean;
};

/**
 * Cache Manager to handle the storage and retrieval of auth tokens and
 * associated data.
 */
export class AuthCacheManager<TAuthUser> {
  public constructor(
    private storage: IStorage,
    private keyManifest?: KeyManifest,
  ) {}

  /**
   * Store an entry in the cache.
   */
  public async set(entry: IAuthCacheEntry<TAuthUser>): Promise<void> {
    const key = new AuthCacheKey({
      clientId: entry.clientId,
      sessionId: entry.sessionId,
    });

    const wrappedEntry = this.wrapCacheEntry(entry);

    await this.storage.set(key.toString(), wrappedEntry);
    await this.keyManifest?.add(key.toString());
  }

  /**
   * Retrieve an entry from the cache.
   */
  public async get(
    keyData: IAuthCacheKeyData,
    expiryAdjustmentSeconds = DEFAULT_EXPIRY_ADJUSTMENT_SECONDS,
  ): Promise<IAuthCacheEntry<TAuthUser> | undefined> {
    const key = new AuthCacheKey(keyData).toString();
    const wrappedEntry =
      this.storage.get<IWrappedCacheEntry<IAuthCacheEntry<TAuthUser>>>(key);

    if (!wrappedEntry) {
      return undefined;
    }

    const now = Date.now();
    const nowInSeconds = Math.floor(now / 1000);

    if (
      wrappedEntry.expiresAt !== undefined &&
      wrappedEntry.expiresAt - expiryAdjustmentSeconds < nowInSeconds
    ) {
      this.storage.delete(key);
      await this.keyManifest?.remove(key);

      return undefined;
    }

    return wrappedEntry.body;
  }

  /**
   * Clear the cache.
   */
  public async clear(clientId?: string, sessionId?: string): Promise<void> {
    const keys = await this.getCacheKeys(clientId, sessionId);
    if (!keys) {
      return;
    }

    await keys.reduce(async (memo, key) => {
      await memo;
      this.storage.delete(key);
    }, Promise.resolve());

    await this.keyManifest?.clear();
  }

  private wrapCacheEntry(
    entry: IAuthCacheEntry<TAuthUser>,
  ): IWrappedCacheEntry<IAuthCacheEntry<TAuthUser>> {
    const now = Date.now();
    const expiresAt =
      entry.expiresIn !== undefined
        ? Math.floor(now / 1000) + entry.expiresIn
        : undefined;

    return {
      body: entry,
      expiresAt,
    };
  }

  public async getCacheKeys(
    clientId?: string,
    sessionId?: string,
  ): Promise<Array<string> | undefined> {
    const keys = this.keyManifest
      ? (await this.keyManifest.all())?.keys
      : this.storage.allKeys
        ? this.storage.allKeys()
        : undefined;

    const filteredKeys = keys?.reduce((acc, key) => {
      if (
        clientId !== undefined &&
        AuthCacheKey.fromString(key).clientId !== clientId
      ) {
        return acc;
      }

      if (
        sessionId !== undefined &&
        AuthCacheKey.fromString(key).sessionId !== sessionId
      ) {
        return acc;
      }

      return [...acc, key];
    }, [] as Array<string>);

    return filteredKeys;
  }
}
